import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { ConversationSidebar } from './conversation-sidebar'
import { ChatMessages } from './chat-messages'
import { ChatInput } from './chat-input'
import { usePolixChat } from '@/hooks/use-polix-chat'
import { useCreateV2Conversation } from '@/hooks/use-conversations-v2'
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation'
import {
  Shield,
  FileCode2,
  BookOpen,
  Layers,
  AlertTriangle,
  X,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { HeaderControls } from '@/components/layout/header-controls'

const suggestions = [
  {
    icon: Shield,
    label: 'Create a policy',
    prompt: 'Create a policy for engineers accessing project docs',
  },
  {
    icon: Layers,
    label: 'ITAR attributes',
    prompt: 'What attributes do I need for ITAR compliance?',
  },
  {
    icon: FileCode2,
    label: 'RBAC templates',
    prompt: 'Show me RBAC policy templates',
  },
  {
    icon: BookOpen,
    label: 'Combining algorithms',
    prompt: 'Explain deny-overrides combining algorithm',
  },
]

export function ChatPage() {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const createConversation = useCreateV2Conversation()
  const { messages, sendMessage, stop, status, error, isStreaming, currentAgent } =
    usePolixChat(conversationId)

  const pendingMessageRef = React.useRef<string | null>(null)
  const [errorDismissed, setErrorDismissed] = React.useState(false)
  const [hasStarted, setHasStarted] = React.useState(false)

  // Reset dismissed state when error clears
  React.useEffect(() => {
    if (!error) setErrorDismissed(false)
  }, [error])

  // Pick up pending message from in-page ref (internal flow)
  React.useEffect(() => {
    if (conversationId && pendingMessageRef.current) {
      const text = pendingMessageRef.current
      pendingMessageRef.current = null
      sendMessage({ text })
    }
  }, [conversationId, sendMessage])

  // Pick up pending message from sessionStorage (launchpad flow)
  React.useEffect(() => {
    if (!conversationId) return
    const pending = sessionStorage.getItem('polix-ai-pending-message')
    if (!pending) return
    sessionStorage.removeItem('polix-ai-pending-message')
    sessionStorage.removeItem('polix-ai-pending-rich-payload')
    sendMessage({ text: pending })
  }, [conversationId, sendMessage])

  // If navigating to an existing conversation with messages, skip welcome
  React.useEffect(() => {
    if (messages.length > 0) {
      setHasStarted(true)
    }
  }, [messages.length])

  const handleSend = async (text: string) => {
    setHasStarted(true)
    if (!conversationId) {
      const result = await createConversation.mutateAsync({})
      pendingMessageRef.current = text
      navigate(`/polix-ai/${result.id}`, { replace: true })
    } else {
      sendMessage({ text })
    }
  }

  const showWelcome = !hasStarted && messages.length === 0

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex h-10 shrink-0 items-center justify-between border-b px-3">
        <SidebarTrigger className="-ml-1" />
        <HeaderControls />
      </header>
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0 overflow-hidden">
        <ResizablePanel id="sidebar" defaultSize="20%" minSize="15%" maxSize="28%">
          <ConversationSidebar activeStreamingId={isStreaming ? conversationId : undefined} />
        </ResizablePanel>
        <ResizableHandle className="w-px bg-border/50 hover:bg-primary/20 transition-colors" />
        <ResizablePanel id="chat" defaultSize="80%" minSize="60%">
          <LayoutGroup>
            <div className="flex flex-col h-full min-h-0 min-w-0 bg-background">
              <AnimatePresence mode="wait">
                {showWelcome ? (
                  <motion.div
                    key="welcome"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -16, transition: { duration: 0.25 } }}
                    className="flex flex-col flex-1 min-h-0 items-center justify-center px-6 py-8"
                  >
                    <WelcomeHero onSend={handleSend} status={status} stop={stop} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="chat"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col flex-1 min-h-0"
                  >
                    <Conversation className="flex-1 min-h-0">
                      <ConversationContent className="gap-6 px-6 py-8">
                        <div className="max-w-3xl mx-auto w-full">
                          <ChatMessages
                            messages={messages}
                            isStreaming={isStreaming}
                            currentAgent={currentAgent}
                            onSendMessage={handleSend}
                          />
                        </div>
                      </ConversationContent>
                      <ConversationScrollButton />
                    </Conversation>
                    {error && !errorDismissed && (
                      <div className="mx-6 mb-2 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
                        <AlertTriangle className="size-4 shrink-0" />
                        <span className="flex-1">
                          {error.message || 'An error occurred while streaming the response.'}
                        </span>
                        <button
                          onClick={() => setErrorDismissed(true)}
                          className="shrink-0 p-0.5 hover:opacity-70"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    )}
                    <ChatInput
                      onSend={handleSend}
                      onStop={stop}
                      status={status}
                      variant="chat"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </LayoutGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

interface WelcomeHeroProps {
  onSend: (text: string) => void
  status: ReturnType<typeof usePolixChat>['status']
  stop: () => void
}

function WelcomeHero({ onSend, status, stop }: WelcomeHeroProps) {
  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto text-center gap-8">
      {/* Hero text */}
      <div className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          What can I help you with?
        </h1>
        <p className="text-muted-foreground text-base leading-relaxed max-w-md mx-auto">
          Your AI-powered companion for access control. Create policies,
          discover sensitive data, analyze compliance, and manage your
          security posture — all through natural conversation.
        </p>
      </div>

      {/* Centered input */}
      <ChatInput
        onSend={onSend}
        onStop={stop}
        status={status}
        variant="welcome"
      />

      {/* Horizontal suggestion chips */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {suggestions.map((s) => (
          <button
            key={s.prompt}
            onClick={() => onSend(s.prompt)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1.5 text-sm text-muted-foreground transition-[border-color,background-color,color] duration-200 hover:border-primary/30 hover:bg-accent/50 hover:text-foreground"
          >
            <s.icon className="size-3.5 shrink-0" strokeWidth={1.5} />
            <span>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
